import { parsePublicPathPattern } from "../pathPattern.js";
function routePath(definition) {
    return "pathPattern" in definition ? definition.pathPattern : definition.path;
}
function localizedRoutePath(definition, localeCode) {
    const localizedPaths = "pathPattern" in definition
        ? definition.localizedPathPatterns
        : definition.localizedPaths;
    return localizedPaths?.[localeCode.toLowerCase()] ?? routePath(definition);
}
function exactRobotsDisallowPatterns(path) {
    return [`${path}$`, `${path}?`];
}
function toRobotsDisallowPatterns(path) {
    const { normalizedPattern, segments } = parsePublicPathPattern(path);
    const wildcardSegments = segments.map((segment) => (segment.kind === "static" ? segment.value : "*"));
    const compacted = wildcardSegments.filter((segment, index) => (segment !== "*" || wildcardSegments[index - 1] !== "*"));
    const wildcardPath = compacted.length === 0 ? "/" : `/${compacted.join("/")}`;
    const firstParameterIndex = segments.findIndex((segment) => segment.kind === "parameter");
    if (firstParameterIndex === -1)
        return exactRobotsDisallowPatterns(normalizedPattern);
    const hasOptionalParameterTail = segments
        .slice(firstParameterIndex)
        .every((segment) => segment.kind === "parameter" && segment.modifier !== "");
    if (!hasOptionalParameterTail)
        return [wildcardPath];
    const baseSegments = segments.slice(0, firstParameterIndex).map((segment) => (segment.kind === "static" ? segment.value : "*"));
    const basePath = baseSegments.length === 0 ? "/" : `/${baseSegments.join("/")}`;
    return [...exactRobotsDisallowPatterns(basePath), wildcardPath];
}
function minimizeDisallowPaths(paths) {
    const unique = Array.from(new Set(paths));
    return unique.filter((path, index) => !unique.some((prefix, prefixIndex) => (prefixIndex !== index
        && !prefix.includes("*")
        && !prefix.endsWith("$")
        && path.startsWith(prefix))));
}
function getRouteLiteralPrefix(pathPattern) {
    const { normalizedPattern, segments } = parsePublicPathPattern(pathPattern);
    const firstParameterIndex = segments.findIndex((segment) => segment.kind === "parameter");
    if (firstParameterIndex === -1)
        return {
            dynamic: false,
            value: normalizedPattern,
        };
    const staticSegments = segments.slice(0, firstParameterIndex).map((segment) => (segment.kind === "static" ? segment.value : ""));
    return {
        dynamic: true,
        value: staticSegments.length === 0 ? "/" : `/${staticSegments.join("/")}/`,
    };
}
function getCollapseCandidate(pathPattern) {
    const literalPrefix = getRouteLiteralPrefix(pathPattern).value;
    if (literalPrefix === "/")
        return null;
    return literalPrefix.endsWith("/") ? literalPrefix.slice(0, -1) : literalPrefix;
}
function routeMayUsePrefix(pathPattern, prefix) {
    const literalPrefix = getRouteLiteralPrefix(pathPattern);
    return literalPrefix.value.startsWith(prefix) || (literalPrefix.dynamic && prefix.startsWith(literalPrefix.value));
}
function routeIsCoveredByPrefix(pathPattern, prefix) {
    return getRouteLiteralPrefix(pathPattern).value.startsWith(prefix);
}
function robotsAllowMayUsePrefix(pattern, prefix) {
    const wildcardIndex = pattern.indexOf("*");
    const literalPrefix = (wildcardIndex === -1 ? pattern : pattern.slice(0, wildcardIndex))
        .replace(/\$$/, "");
    return literalPrefix.startsWith(prefix) || prefix.startsWith(literalPrefix);
}
export function getPublicRobotsDisallowPaths(site) {
    const routePaths = site.routerRouteDefinitions.flatMap((definition) => [
        {
            crawl: definition.crawl,
            path: routePath(definition),
        },
        ...(definition.localePolicy === "none" ? [] : site.locales.map((locale) => ({
            crawl: definition.crawl,
            path: site.withLocalePrefix(localizedRoutePath(definition, locale.code), locale.code),
        }))),
    ]);
    const pathGroups = new Map();
    const collapse = site.robots.collapseDisallowPrefixes === true;
    const protectedRoutePaths = [
        ...routePaths
            .filter((route) => route.crawl !== "disallow")
            .map((route) => route.path),
        ...site.getRedirectAliases().map((redirect) => redirect.path),
    ];
    const collapseCandidates = collapse
        ? Array.from(new Set(routePaths
            .filter((route) => route.crawl === "disallow")
            .map((route) => getCollapseCandidate(route.path))
            .filter((path) => path !== null)))
            .filter((prefix) => (!protectedRoutePaths.some((path) => routeMayUsePrefix(path, prefix))
            && !(site.robots.additionalAllow ?? []).some((allow) => (robotsAllowMayUsePrefix(allow, prefix)))))
            .sort((left, right) => left.length - right.length)
        : [];
    for (const route of routePaths) {
        if (route.crawl !== "disallow")
            continue;
        const collapsedPrefix = collapseCandidates.find((prefix) => (routeIsCoveredByPrefix(route.path, prefix)));
        const groupKey = collapsedPrefix ?? getCollapseCandidate(route.path) ?? route.path;
        const group = pathGroups.get(groupKey) ?? [];
        if (collapsedPrefix)
            group.push(collapsedPrefix);
        else
            group.push(...toRobotsDisallowPatterns(route.path));
        pathGroups.set(groupKey, group);
    }
    return minimizeDisallowPaths(Array.from(pathGroups.values()).flat());
}
function renderGroup(group) {
    return [
        ...group.userAgents.map((agent) => `User-agent: ${agent}`),
        ...(group.allow ?? []).map((path) => `Allow: ${path}`),
        ...(group.disallow ?? []).map((path) => `Disallow: ${path}`),
    ];
}
function renderContentSignals(signals) {
    if (!signals)
        return null;
    const values = [
        signals.search === undefined ? null : `search=${signals.search ? "yes" : "no"}`,
        signals.aiInput === undefined ? null : `ai-input=${signals.aiInput ? "yes" : "no"}`,
        signals.aiTrain === undefined ? null : `ai-train=${signals.aiTrain ? "yes" : "no"}`,
    ].filter((value) => value !== null);
    return values.length > 0 ? `Content-Signal: ${values.join(", ")}` : null;
}
function absoluteUrl(siteUrl, path) {
    return `${siteUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}
export function buildPublicRobotsTxt(options) {
    const disallow = minimizeDisallowPaths([
        ...getPublicRobotsDisallowPaths(options.site),
        ...options.site.robots.additionalDisallow ?? [],
    ]);
    const signals = renderContentSignals(options.contentSignals ?? options.site.robots.contentSignals);
    const groups = [{
            userAgents: ["*"],
            allow: ["/", ...options.site.robots.additionalAllow ?? []],
            disallow,
        }, ...options.additionalGroups ?? []];
    const lines = groups.flatMap((group, index) => [
        ...(index === 0 ? [] : [""]),
        ...renderGroup(group),
    ]);
    if (signals)
        lines.push("", signals);
    if (options.host)
        lines.push("", `Host: ${options.host}`);
    const siteUrl = options.siteUrl ?? options.site.origin;
    if (siteUrl)
        for (const sitemapPath of options.sitemapPaths ?? options.site.robots.sitemapPaths ?? ["/sitemap.xml"])
            lines.push(`Sitemap: ${absoluteUrl(siteUrl, sitemapPath)}`);
    return `${lines.join("\n")}\n`;
}
//# sourceMappingURL=index.js.map